/** Max networking snapshot rows fetched per assoc-history search. */
export const ASSOC_HISTORY_MAX_ROWS = 1000;

/** GraphQL count_distinct_field for assoc-history count queries. */
export const ASSOC_HISTORY_COUNT_DISTINCT_FIELD = 'wifi_interfaces.associations.station';

/** Traffic threshold units for assoc-history data filters (binary prefixes). */
export const HIST_DATA_SIZE_UNITS = ['bytes', 'KB', 'MB', 'GB'];

const HIST_DATA_SIZE_UNIT_MULTIPLIERS = {
  bytes: 1,
  KB: 1024,
  MB: 1024 ** 2,
  GB: 1024 ** 3,
};

/** Convert a user-entered traffic threshold to whole bytes for GraphQL filters. */
export function convertHistDataSizeToBytes(value, unit = 'bytes') {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const mult = HIST_DATA_SIZE_UNIT_MULTIPLIERS[unit] ?? 1;
  return Math.ceil(Number(value) * mult);
}

const ASSOC_FIELD_CONNECTED_TIME = 'wifi_interfaces.associations.connected_time';
const ASSOC_FIELD_RX_BYTES = 'wifi_interfaces.associations.rx_bytes';
const ASSOC_FIELD_TX_BYTES = 'wifi_interfaces.associations.tx_bytes';

export function buildNetworkingAssocHistoryFilter(
  fromIso,
  toIso,
  { serial, minConnected, maxConnected, minRx, minTx } = {},
) {
  const conditions = [
    { between: { field: 'datetime', value: { start: fromIso, end: toIso } } },
  ];

  const serialNorm = serial != null ? String(serial).trim().toLowerCase() : '';
  if (serialNorm) {
    conditions.push({ equal: { field: 'device_mac_address', value: serialNorm } });
  }
  if (minConnected != null) {
    conditions.push({ greater_than_or_equal: { field: ASSOC_FIELD_CONNECTED_TIME, value: minConnected } });
  }
  if (maxConnected != null) {
    conditions.push({ less_than_or_equal: { field: ASSOC_FIELD_CONNECTED_TIME, value: maxConnected } });
  }
  if (minRx != null) {
    conditions.push({ greater_than_or_equal: { field: ASSOC_FIELD_RX_BYTES, value: minRx } });
  }
  if (minTx != null) {
    conditions.push({ greater_than_or_equal: { field: ASSOC_FIELD_TX_BYTES, value: minTx } });
  }

  return conditions.length === 1 ? conditions[0] : { and: conditions };
}

export function maskClientMac(mac) {
  if (!mac) return mac;
  const parts = mac.split(':');
  if (parts.length !== 6) return mac;
  return `xx:xx:xx:${parts[3]}:${parts[4]}:${parts[5]}`;
}

export function normalizeClientMac(raw) {
  const s = String(raw || '').toLowerCase().trim().replace(/[:\-.\s]/g, '');
  return /^[0-9a-f]{12}$/.test(s) ? s : '';
}

export function formatClientMac(normalized) {
  if (!normalized || normalized.length !== 12) return normalized || '';
  return normalized.match(/.{2}/g).join(':');
}

export function looksLikeClientMac(raw) {
  return Boolean(normalizeClientMac(raw));
}

export function looksLikeStationDigest(raw) {
  return /^[0-9a-f]{64}$/i.test(String(raw || '').trim());
}

/** Typed MAC on search hits; hide stored digests and leftover plaintext. */
export function displayStoredStation(stationRaw, { searchMac, searchDigest } = {}) {
  const raw = String(stationRaw || '').toLowerCase().trim();
  const qNorm = normalizeClientMac(searchMac);
  if (!qNorm) return '';
  const digest = String(searchDigest || '').toLowerCase().trim();
  if (digest && raw === digest) return formatClientMac(qNorm);
  if (normalizeClientMac(raw) === qNorm) return formatClientMac(qNorm);
  return '';
}

export function flattenNetworkingSnapshots(snapshots, { includeMetrics = true } = {}) {
  const flat = [];
  for (const snap of snapshots) {
    const radioBand = {};
    if (includeMetrics) {
      for (const r of snap.radio || []) {
        if (r.name && r.band) radioBand[r.name] = r.band;
      }
    }
    const serial = (snap.device_mac_address || '').toLowerCase();
    for (const iface of snap.wifi_interfaces || []) {
      if (iface.mode !== 'ap' || !iface.public) continue;
      const band = includeMetrics ? (radioBand[iface.radio_name] || '') : '';
      for (const assoc of iface.associations || []) {
        if (!assoc.station) continue;
        const row = {
          reported_at: snap.datetime,
          serial,
          interface: iface.name,
          stationRaw: String(assoc.station).toLowerCase(),
        };
        if (includeMetrics) {
          row.ssid = iface.ssid || '';
          row.band = band;
          row.bssid = assoc.bssid || '';
          row.rssi = assoc.rssi || '';
          row.rx_bytes = assoc.rx_bytes ?? 0;
          row.tx_bytes = assoc.tx_bytes ?? 0;
          row.tx_failed = assoc.tx_failed ?? 0;
          row.connected_time = assoc.connected_time ?? 0;
          row.rx_rate = assoc.rx_rate || {};
          row.tx_rate = assoc.tx_rate || {};
        }
        flat.push(row);
      }
    }
  }
  return flat;
}

export function mergeAssocHistoryRows(flat, { maskStation = true } = {}) {
  const groups = {};
  for (const r of flat) {
    const k = `${r.serial}\0${r.stationRaw}`;
    const g = groups[k];
    const ts = new Date(r.reported_at).getTime();
    if (!g) {
      groups[k] = { times: new Set([ts]), first: ts, last: ts, latest: r };
    } else {
      g.times.add(ts);
      if (ts < g.first) g.first = ts;
      if (ts > g.last) {
        g.last = ts;
        g.latest = r;
      }
    }
  }

  return Object.values(groups).map((g) => {
    const row = g.latest;
    const merged = {
      reported_at: new Date(g.last).toISOString(),
      first_reported_at: new Date(g.first).toISOString(),
      reports_count: g.times.size,
      serial: row.serial,
      interface: row.interface,
      stationRaw: row.stationRaw,
    };
    if (row.ssid !== undefined) {
      Object.assign(merged, {
        ssid: row.ssid,
        band: row.band,
        bssid: row.bssid,
        station: '',
        rssi: row.rssi,
        rx_bytes: row.rx_bytes,
        tx_bytes: row.tx_bytes,
        tx_failed: row.tx_failed,
        connected_time: row.connected_time,
        rx_rate: row.rx_rate,
        tx_rate: row.tx_rate,
      });
    } else {
      merged.station = '';
    }
    return merged;
  });
}

// Numeric/metric filters apply per association record. GraphQL filters whole
// networking rows, so a returned row can still carry stations outside the
// requested range; re-apply the same bounds client-side before merging so the
// surviving (and displayed-latest) record always satisfies the thresholds.
export function filterAssocMetricRows(rows, filters = {}) {
  const minConnected = filters.minConnected;
  const maxConnected = filters.maxConnected;
  const minRx = filters.minRx;
  const minTx = filters.minTx;

  return rows.filter((r) => {
    if (minConnected != null && Number(r.connected_time) < minConnected) return false;
    if (maxConnected != null && Number(r.connected_time) > maxConnected) return false;
    if (minRx != null && Number(r.rx_bytes) < minRx) return false;
    if (minTx != null && Number(r.tx_bytes) < minTx) return false;
    return true;
  });
}

// Identity filters (lamp serial, station MAC) match on stable keys, so they can
// run after merge without the array-leakage concern metric filters have.
export function filterAssocHistoryRows(rows, filters = {}) {
  const serialFilt = (filters.serial || '').trim().toLowerCase();
  const qNorm = normalizeClientMac(filters.station);
  const digest = String(filters.stationDigest || '').toLowerCase().trim();
  const stationFilt = (filters.station || '').trim();

  return rows.filter((r) => {
    if (serialFilt && !(r.serial || '').includes(serialFilt)) return false;
    if (stationFilt) {
      const raw = String(r.stationRaw || '').toLowerCase().trim();
      if (qNorm) {
        if (digest && raw === digest) return true;
        if (normalizeClientMac(raw) === qNorm) return true;
        return false;
      }
      return false;
    }
    return true;
  });
}

export function processNetworkingAssocSnapshots(snapshots, filters = {}) {
  const flat = flattenNetworkingSnapshots(snapshots, { includeMetrics: true });
  const metricFiltered = filterAssocMetricRows(flat, filters);
  const merged = mergeAssocHistoryRows(metricFiltered, { maskStation: true });
  const filtered = filterAssocHistoryRows(merged, filters);
  for (const r of filtered) {
    r.station = displayStoredStation(r.stationRaw, {
      searchMac: filters.station,
      searchDigest: filters.stationDigest,
    });
  }
  filtered.sort((a, b) => new Date(b.reported_at) - new Date(a.reported_at));
  return filtered;
}

export function parseHistNumericFilters({
  minConnectedTime,
  maxConnectedTime,
  minRxBytes,
  minTxBytes,
  dataSizeUnit = 'bytes',
}) {
  const minConnected = minConnectedTime.trim() === '' ? null : Number(minConnectedTime);
  const maxConnected = (maxConnectedTime ?? '').trim() === '' ? null : Number(maxConnectedTime);
  const minRxInput = minRxBytes.trim() === '' ? null : Number(minRxBytes);
  const minTxInput = minTxBytes.trim() === '' ? null : Number(minTxBytes);

  if (minConnected != null && (!Number.isFinite(minConnected) || minConnected < 0)) {
    return { error: 'Min connected time filter must be a non-negative number (seconds).' };
  }
  if (maxConnected != null && (!Number.isFinite(maxConnected) || maxConnected < 0)) {
    return { error: 'Max connected time filter must be a non-negative number (seconds).' };
  }
  if (minConnected != null && maxConnected != null && maxConnected < minConnected) {
    return { error: 'Max connected time must be greater than or equal to min connected time.' };
  }
  if (minRxInput != null && (!Number.isFinite(minRxInput) || minRxInput < 0)) {
    return { error: 'Min RX must be a non-negative number.' };
  }
  if (minTxInput != null && (!Number.isFinite(minTxInput) || minTxInput < 0)) {
    return { error: 'Min TX must be a non-negative number.' };
  }

  const minRx = minRxInput == null ? null : convertHistDataSizeToBytes(minRxInput, dataSizeUnit);
  const minTx = minTxInput == null ? null : convertHistDataSizeToBytes(minTxInput, dataSizeUnit);
  return { minConnected, maxConnected, minRx, minTx };
}
